import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { PasswordInput } from "@/components/PasswordInput";

export function Login() {
  const { session, signIn } = useAuth();
  const loc = useLocation() as { state?: { from?: { pathname?: string } } };
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (session) return <Navigate to={loc.state?.from?.pathname ?? "/"} replace />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    const { error } = await signIn(email, password);
    setBusy(false);
    if (error) setError(error); else nav("/", { replace: true });
  }

  return (
    <AuthScaffold>
      <h1 className="mb-1 text-2xl font-bold tracking-tight">Welcome back</h1>
      <p className="mb-6 text-sm text-gray2">Sign in to Scenta HQ to clock in, chat, and manage your work.</p>
      {error && <div className="mb-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label">Email</label>
          <input className="input" type="email" required value={email} onChange={e => setEmail(e.target.value)} autoFocus autoComplete="email" />
        </div>
        <div>
          <label className="label">Password</label>
          <PasswordInput required minLength={6} value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
        </div>
        <button className="btn w-full btn-lg" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
      </form>
      {/* HQ-AUTH-REGISTER-001: no Register link — accounts come from an
          administrator's invitation, not from signing up. /register still
          exists and says so, for anyone arriving on an old link. */}
      <div className="mt-6 text-center text-sm">
        <Link to="/forgot-password" className="text-teal-dark hover:underline">Forgot password?</Link>
      </div>
    </AuthScaffold>
  );
}

export function AuthScaffold({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-offwhite via-white to-teal-mint/40 p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center"><Brand /></div>
        <div className="card shadow-soft">{children}</div>
        <p className="mt-4 text-center text-xs text-gray2">Scenta · Built for the team.</p>
      </div>
    </div>
  );
}
